import React, {FC, useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {dashboardService} from "../../../store/actions";
import LOC from "../../../images/code_loc.svg";
import SCD from "../../../images/bs_scan_duration.svg";
import BAvatar from "../../../components/b-avatar";
import BCopy from "../../../components/b-copy";
import BQR from "../../../components/b-qr";
import {useTranslation} from "react-i18next";
import useCommon from "../../../common/hooks/useCommon";
import useSize from "../../../common/hooks/useSize";
import ScoreChart from "./common/score-chart";
import {truncate} from "../../../common/utils/function";
import ProgressRing from "./common/progressbar";
import BSkeletonCard from "../../../components/b-skeleton/b-skeleton-card";
import BSkeleton from "../../../components/b-skeleton";

const AuditPage: FC = () => {
    const {address, token} = useParams();
    const [severityData, setSeverityData] = useState<any[]>([]);
    const [auditData, setAuditData] = useState<any | null>(null);
    const [score, setScore] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [totalVulnerabilities, setTotalVulnerabilities] = useState<number>(0);
    const {t, i18n} = useTranslation();
    const {getXDC, get0x} = useCommon();

    const {width} = useSize();
    const isMobile = width <= 768;

    const severityColors: { [key: string]: string } = {
        critical: "rgb(150, 13, 0)",
        gas: "rgb(247, 149, 180)",
        high: "rgb(255, 92, 0)",
        informational: "rgb(160, 174, 192)",
        low: "rgb(56, 203, 137)",
        medium: "rgb(255, 230, 0)",
    };

    const fetchData = async () => {
        try {
            const response = await dashboardService.getAuditData(address || token);
            if (response) {
                const apiData = response;
                if (apiData.issue_severity_distribution) {
                    const severityDistribution: { [key: string]: number } =
                        apiData.issue_severity_distribution;

                    const processedSeverityData = Object.keys(severityDistribution).map(
                        (key) => ({
                            name: key.charAt(0).toUpperCase() + key.slice(1),
                            y: severityDistribution[key],
                            color: severityColors[key],
                        })
                    );

                    setSeverityData(processedSeverityData);

                    const total = Object.values(severityDistribution).reduce(
                        (sum: number, val: number) => sum + val,
                        0
                    );
                    setTotalVulnerabilities(total);
                } else {
                    console.error(
                        "issue_severity_distribution not found in the response"
                    );
                }

                setAuditData(apiData);

                if (apiData.score_v2) {
                    setScore(parseFloat(apiData.score_v2));
                } else {
                    console.error("score_v2 not found in the response");
                    setScore(0);
                }

                setLoading(false);
                setError(false);
            } else {
                console.error("API call failed or returned empty response");
                setLoading(false);
                setError(true);
            }
        } catch (error) {
            console.error("Error fetching data", error);
            setLoading(false);
            setError(true);
        }
    };

    useEffect(() => {
        fetchData();
    }, [address, token]);

    const severityChartOptions = {
        chart: {
            type: "pie",
        },
        title: {
            useHTML: true,
            text: `<span class="bs-font">Issue Severity Distribution</span>`,
        },
        subtitle: {
            useHTML: true,
            text: `<span class="bs-font-secondary">Source: SolidityScan <span>`,
            align: "center",
        },
        exporting: {enabled: false},
        credits: {enabled: false},
        plotOptions: {
            pie: {
                innerSize: "60%",
                showInLegend: false,
                dataLabels: {
                    useHTML: true,
                    formatter: function (this: any) {
                        return `<tspan style="fill: var(--font); color: var(--font); font-size: 12px; line-height: 18px;">${this.point.name}</tspan>`;
                    },
                    style: {
                        textOutline: "none",
                    },
                },
            },
        },
        series: [
            {
                name: "Issues",
                colorByPoint: true,
                data: severityData,
            },
        ],
    };

    const getScoreClass = (rating: string) => {
        switch (rating) {
            case "Low":
                return "text-danger";
            case "Average":
                return "text-warning";
            case "High":
                return "text-success";
            case "Excellent":
                return "text-primary";
            default:
                return "text-muted";
        }
    };

    // if (loading || error) {
    //   return (
    //     <React.Fragment>
    //       <div>Your contract is being Validated ...</div>
    //       <div>Your contract is being Scanned ...</div>
    //     </React.Fragment>
    //   );
    // }

    return (
        <React.Fragment>
            <div className="row">
                <div className="col-lg-12">
                    <div className="bs-card h-100">
                        <div className="bs-card-body p-3">
                            <div className="row">
                                <div className={`col-lg-6 ${isMobile ? "mb-3" : ""}`}>
                                    <div className="bs-card h-100">
                                        <div
                                            className="bs-card-body p-3"
                                            style={{position: "relative"}}
                                        >
                                            <HighchartsReact
                                                highcharts={Highcharts}
                                                options={severityChartOptions}
                                            />
                                            {!isMobile && totalVulnerabilities !== null && (
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: "50%",
                                                        left: "50%",
                                                        transform: "translate(-50%, -50%)",
                                                        fontSize: "16px",
                                                        fontWeight: "bold",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    <span style={{fontSize: "24px"}}>
                                                        {totalVulnerabilities} <br/>
                                                    </span>
                                                    Total Vulnerabilities <br/>
                                                    found
                                                </div>
                                            )}
                                            <div className="d-flex justify-content-center">
                                                <a
                                                    href={auditData?.scanner_reference_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <button className="btn btn-sm bs-btn me-2">
                                                        View Audit Report PDF{" "}
                                                        <i className="bi bi-arrow-right"></i>
                                                    </button>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-6">
                                    <div className="bs-card h-100 p-3">
                                        <div className="d-flex align-items-center justify-content-center mb-3">
                                            <div style={{marginRight: "10px", marginTop: "5px"}}>
                                                <BAvatar
                                                    address={getXDC(address || token)}
                                                    diameter={32}
                                                />
                                            </div>
                                            <div>
                                                <h2 className="fs-6 lh-base fw-bold">
                                                    {t("Contract Address")}
                                                </h2>
                                                <span className="text-muted fw-normal">
                                                    #
                                                    {isMobile
                                                        ? truncate(getXDC(address || token))
                                                        : getXDC(address || token)}{" "}
                                                    <BCopy value={getXDC(address || token)}/>{" "}
                                                    <BQR value={getXDC(address || token)}/>
                                                </span>
                                            </div>
                                        </div>

                                        <div className="row my-3">
                                            <div className="col-lg-4 col-md-6 mb-3">
                                                <div className="bs-card p-3 h-100">
                                                    <h2 className="text-uppercase text-secondary mb-0">
                                                        Security Score
                                                    </h2>
                                                    {loading ? (
                                                        <BSkeleton width={40} height={30}/>
                                                    ) : (
                                                        <React.Fragment>
                                                            <span className="fs-5">
                                                                <ProgressRing apiData={auditData}/>
                                                                {score?.toFixed(2)}/100
                                                            </span>
                                                        </React.Fragment>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-lg-4 col-md-6 mb-3">
                                                <div className="bs-card p-3 h-100">
                                                    <h2 className="text-uppercase text-secondary mb-0">
                                                        Scan duration
                                                    </h2>
                                                    {loading ? (
                                                        <BSkeleton width={40} height={30}/>
                                                    ) : (
                                                        <React.Fragment>
                                                            <span className="fs-5">
                                                                <img
                                                                    src={SCD}
                                                                    alt="Scan duration"
                                                                    className="me-2"
                                                                />
                                                                {auditData?.scan_time_taken} secs
                                                            </span>
                                                        </React.Fragment>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-lg-4 col-md-6 mb-3">
                                                <div className="bs-card p-3 h-100">
                                                    <h2 className="text-uppercase text-secondary mb-0">
                                                        Lines of code
                                                    </h2>
                                                    {loading ? (
                                                        <BSkeleton width={40} height={30}/>
                                                    ) : (
                                                        <React.Fragment>
                                                            <span className="fs-5">
                                                                <img
                                                                    src={LOC}
                                                                    alt="Lines of code"
                                                                    className="me-2"
                                                                />
                                                                {auditData?.lines_analyzed_count}
                                                            </span>
                                                        </React.Fragment>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {loading || error ? (
                                            <BSkeletonCard width="500px" height="600"/>
                                        ) : (
                                            <div className="score-chart-container row align-items-center g-4">
                                                <div className="col-md-6 col-lg-4 d-flex justify-content-center chart-wrapper">
                                                    <ScoreChart apiData={auditData}/>
                                                </div>
                                                <div className="col-md-6 col-lg-8 text-center text-md-start">
                                                    <h4 className="fs-4 fw-bold text-primary mb-2 score-heading">
                                                        Your Security Score:{" "}
                                                        <span
                                                            className={`${getScoreClass(
                                                                auditData?.score_rating
                                                            )}`}
                                                        >
                                                            {auditData?.score_rating}
                                                        </span>
                                                    </h4>
                                                    <p className="fs-6 mb-0 score-description">
                                                        The <span className="fw-bold">SolidityScan</span>{" "}
                                                        score is calculated based on lines of code and
                                                        weights assigned to each issue depending on severity
                                                        and confidence. To improve your score, view the
                                                        detailed result and leverage the remediation
                                                        solutions provided.
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default AuditPage;
